import LockOutlined from '@mui/icons-material/LockOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import BlockSettingsToolButton from './BlockSettingsToolButton';
import { useCurrentUser } from '../../../../providers/UserProvider';
import { useBlockLockStatus } from '../../hooks/useBlockLockStatus';
import { useOpenBlockSettings } from './useOpenBlockSettings';
import { useNewBlockStyleSettings } from 'components/editor/hooks/useNewBlockSettings';
import type { LockToolProps } from './LockTool.types';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';

type HandleLockToggleParams = {
  isLocked: boolean;
  blockType: LockToolProps['blockType'];
  blockId: string;
  isEditMode: LockToolProps['isEditMode'];
  setIsEditMode: LockToolProps['setIsEditMode'];
  openBlockSettings: (blockType: GridBlockType, blockId: string) => void;
  closeBlockSettings: () => void;
  openTableDefaultSettings: (blockId: string) => void;
};

const handleLockToggle = ({
  isLocked,
  blockType,
  blockId,
  isEditMode,
  setIsEditMode,
  openBlockSettings,
  closeBlockSettings,
  openTableDefaultSettings,
}: HandleLockToggleParams) => {
  if (isLocked) {
    if (blockType) {
      switch (blockType) {
        case GridBlockType.TEXT:
        case GridBlockType.IMAGE:
        case GridBlockType.VIDEO:
        case GridBlockType.SHAPE:
          openBlockSettings(blockType, blockId);
          break;

        case GridBlockType.TABLE:
          openTableDefaultSettings(blockId);
          break;
      }
    }
  } else {
    closeBlockSettings();
    if (isEditMode && setIsEditMode) {
      setIsEditMode(false);
    }
  }
};

const LockToolWithUndoRedo = ({ blockId, isEditMode, setIsEditMode, blockType }: LockToolProps) => {
  const sectionId = useAppSelector((state) => selectSectionIdByBlockId(state, blockId));
  const { getBlockStyleById, addBlock, updateBlockStyleWithUndo } = useNewBlockStyleSettings(blockId, sectionId ?? '');
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser.id;
  const { openBlockSettings, closeBlockSettings, openTableDefaultSettings } = useOpenBlockSettings();

  const { isLocked } = useBlockLockStatus(blockId);

  const toggleLockStatus = (e: React.MouseEvent) => {
    e.stopPropagation();

    const newLockedStatus = isLocked ? null : userId;

    const blockStyle = getBlockStyleById(blockId);

    if (!blockStyle) {
      addBlock(blockId, false);
    }

    updateBlockStyleWithUndo({ lockedBy: newLockedStatus });

    handleLockToggle({
      isLocked,
      blockType,
      blockId,
      isEditMode,
      setIsEditMode,
      openBlockSettings,
      closeBlockSettings,
      openTableDefaultSettings,
    });
  };

  return (
    <BlockSettingsToolButton title={isLocked ? 'Unlock block' : 'Lock block'} onClick={toggleLockStatus} data-testid="lock-tool-button">
      {isLocked ? (
        <LockOutlined width={24} height={24} fontSize="small" color="inherit" />
      ) : (
        <LockOpenOutlined width={24} height={24} fontSize="small" color="inherit" />
      )}
    </BlockSettingsToolButton>
  );
};

export default function LockTool({ blockId, isEditMode, setIsEditMode, blockType }: LockToolProps) {
  return <LockToolWithUndoRedo blockId={blockId} isEditMode={isEditMode} setIsEditMode={setIsEditMode} blockType={blockType} />;
}
